import Badge from "components/atoms/badge";
import { DateIcon, TimeIcon } from "components/atoms/Icon";
import moment from "moment";
import Table from "components/molecules/Table";
import { get } from "utils/request";
import { getMerchDropDownloadEndpoint } from "apis/merchdrop";
import { downloadOnClick } from "utils";
import { useLoaderContext } from "components/context/LoaderContext";
import { toast } from "react-toastify";
import { MouseEventHandler, useCallback } from "react";
import { DATE_FORMAT, TIME_FORMAT, TOAST_CONFIG, activationKeyUserDescriptionMap } from "../../../../../constants";

const AirdropActivationStatusLabelMap = {
  0: ["Draft", "#060F1B", "white"],
  1: ["Scheduling", "#060F1B", "white"],
  3: ["Scheduled", "#060F1B", "white"],
  4: ["Started", "#060F1B", "white"],
  5: ["Airdropped", "white", "#060F1B"],
  6: ["Closed", "white", "#060F1B"],
};

const getStatusBadge = (code: MementoStatus) => {
  if (code >= 0 && code < 7 && code !== 2) {
    return (
      <Badge
        className="border border-white px-3 py-0.5 text-xxs 2xl:px-5 2xl:py-1 2xl:text-xs"
        content={AirdropActivationStatusLabelMap[code][0]}
        forGroundCOlor={AirdropActivationStatusLabelMap[code][1]}
        backgroundColor={AirdropActivationStatusLabelMap[code][2]}
      />
    );
  }

  return null;
};

const getDropStatus = (Memento: Memento) => Memento.drop_status;

type Props = {
  mementosForMagnet: Memento[];
  onClickManage: (mementoId: string, mementoActivationKey: string) => void;
  setMementoForViewingMemento: (Memento: string) => void;
  handleViewPollResults: (pollId: string) => void;
  handleCreateActivation: MouseEventHandler;
  isMagnetPublished: boolean;
  handleShareRequestPreview: (id: string) => void;
};

const ActivationTableHeaders = [
  "ACTIVATION",
  "STATUS",
  "APPLICATION",
  "START DATE/TIME",
  "END DATE/TIME",
  "DROP STATUS",
  "INTERACTIONS",
  "ACTIONS",
];

function ActivationTable({
  mementosForMagnet,
  onClickManage,
  setMementoForViewingMemento,
  handleViewPollResults,
  handleCreateActivation,
  isMagnetPublished,
  handleShareRequestPreview,
}: Props) {
  const { setLoading } = useLoaderContext();

  const handleDownloadMerchDropResults = useCallback(async (Memento: Memento) => {
    if (Memento.drop_status === "0") {
      toast.info("None of the users have claimed merch drop.", TOAST_CONFIG);
      return;
    }

    try {
      setLoading(true);
      const resp: Blob = await get(getMerchDropDownloadEndpoint(Memento.magnet_id, Memento.id), {
        requestConfig: {
          responseType: "blob",
        },
        headers: {
          "Content-Type": "application/octate-stream",
        },
      });
      downloadOnClick(URL.createObjectURL(resp));
    } finally {
      setLoading(false);
    }
  }, []);

  const getMementoTableButton = (Memento: Memento) => {
    switch (Memento.status) {
      case 0:
        return <Table.Button label="EDIT" handleOnClick={() => onClickManage(Memento.id, Memento.activation_key)} />;
      case 1:
        return null;
      case 3:
        if (Memento.activation_key === "social_share")
          return (
            <Table.Button
              label="MANAGE"
              handleOnClick={() => {
                handleShareRequestPreview(Memento.id);
              }}
            />
          );
        if (
          Memento.activation_key === "merch_drop" ||
          Memento.activation_key === "claim_code" ||
          Memento.activation_key === "mem_drop"
        )
          return (
            <Table.Button
              label="MANAGE"
              handleOnClick={() => {
                setMementoForViewingMemento(Memento.id);
              }}
            />
          );
        return null;
      case 4:
        if (Memento.activation_key === "poll")
          return (
            <Table.Button
              label="VIEW RESULTS"
              handleOnClick={() => {
                handleViewPollResults(Memento.id);
              }}
            />
          );
        if (
          Memento.activation_key === "mem_drop" ||
          Memento.activation_key === "merch_drop" ||
          Memento.activation_key === "claim_code"
        )
          return (
            <Table.Button
              label="MANAGE"
              handleOnClick={() => {
                setMementoForViewingMemento(Memento.id);
              }}
            />
          );
        if (Memento.activation_key === "social_share")
          return (
            <Table.Button
              label="MANAGE"
              handleOnClick={() => {
                handleShareRequestPreview(Memento.id);
              }}
            />
          );

        return null;
      case 5:
        if (Memento.activation_key === "mem_drop")
          return (
            <Table.Button
              label="VIEW"
              handleOnClick={() => {
                setMementoForViewingMemento(Memento.id);
              }}
            />
          );
        if (Memento.activation_key === "merch_drop") {
          return <Table.Button label="VIEW CLAIMS" handleOnClick={() => handleDownloadMerchDropResults(Memento)} />;
        }
        return null;
      case 6:
        if (Memento.activation_key === "poll")
          return (
            <Table.Button
              label="VIEW RESULTS"
              handleOnClick={() => {
                handleViewPollResults(Memento.id);
              }}
            />
          );
        return null;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col ">
      <div className="mt-2 w-full min-w-[900px] rounded-2xl border border-secondary">
        <Table>
          <Table.Header>
            <Table.Row isHeaderRow>
              {ActivationTableHeaders.map((label, idx) => (
                <Table.HeaderCell
                  additionalClasses={idx === 0 ? "ml-7" : undefined}
                  align={idx === ActivationTableHeaders.length - 1 ? "center" : "left"}
                  label={label}
                  key={label}
                />
              ))}
            </Table.Row>
          </Table.Header>

          {mementosForMagnet.length === 0 ? (
            <Table.EmptyState
              onClick={handleCreateActivation}
              emptyStatefor="Activation"
              isDisabled={!isMagnetPublished}
              title={isMagnetPublished ? "Create Activation" : "Publish Magnet to create Activation"}
            />
          ) : (
            <Table.Body>
              {mementosForMagnet.map((Memento) => (
                <Table.Row key={Memento.id}>
                  <Table.Description textAlign="start">
                    <div className=" flex items-center justify-center gap-1 2xl:gap-2">
                      <div className="h-6 w-6 flex-none">
                        <img className="h-6 w-6 flex-none" src={Memento.activation_image_url} alt={Memento.name} />
                      </div>
                      <div className="w-[150px]">
                        <p
                          className="font-satoshi text-base font-semibold
                           leading-4 text-white text-opacity-70"
                        >
                          {activationKeyUserDescriptionMap[Memento.activation_key]}
                        </p>
                        <p
                          className="line-clamp-1 break-all font-satoshi text-xs font-semibold 
                        leading-6 text-white 2xl:text-sm"
                        >
                          {Memento.name}
                        </p>
                      </div>
                    </div>
                  </Table.Description>
                  <Table.Description textAlign="start">
                    <div className="flex justify-start">
                      <span className="font-satoshi text-lg font-semibold">{getStatusBadge(Memento.status)}</span>
                    </div>
                  </Table.Description>
                  <Table.Description textAlign="start">
                    <span
                      className="font-satoshi text-xs
                   font-semibold text-white 2xl:text-sm"
                    >
                      {Memento.activation_name}
                    </span>
                  </Table.Description>
                  <Table.Description textAlign="start">
                    <div className="flex justify-start text-white">
                      <div>
                        {Memento.status === 0 ? (
                          "-"
                        ) : (
                          <>
                            <span
                              className="flex items-center gap-1 font-satoshi text-xs font-semibold
                   2xl:gap-2 2xl:text-sm"
                            >
                              <DateIcon className="h-4 w-4 2xl:h-5 2xl:w-5" color="text-secondary" />
                              {moment(Memento.start_time).format(DATE_FORMAT)}
                            </span>
                            <span
                              className="flex items-center gap-1 font-satoshi text-xs font-semibold 
                   2xl:gap-2 2xl:text-sm"
                            >
                              <TimeIcon className="h-4 w-4 2xl:h-5 2xl:w-5" color="text-secondary" />
                              {moment(Memento.start_time).format(TIME_FORMAT)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </Table.Description>
                  <Table.Description textAlign="start">
                    <div className="flex justify-start text-white">
                      <div>
                        {Memento.status !== 0 && Memento.end_time ? (
                          <>
                            <span
                              className="flex items-center gap-1 font-satoshi text-xs font-semibold
                 2xl:gap-2 2xl:text-sm"
                            >
                              <DateIcon className="h-4 w-4 2xl:h-5 2xl:w-5" color="text-secondary" />
                              {moment(Memento.end_time).format(DATE_FORMAT)}
                            </span>
                            <span
                              className="flex items-center gap-1 font-satoshi text-xs font-semibold 
                 2xl:gap-2 2xl:text-sm"
                            >
                              <TimeIcon className="h-4 w-4 2xl:h-5 2xl:w-5" color="text-secondary" />
                              {moment(Memento.end_time).format(TIME_FORMAT)}
                            </span>
                          </>
                        ) : (
                          "-"
                        )}
                      </div>
                    </div>
                  </Table.Description>

                  <Table.Description>
                    <span
                      className="font-satoshi text-xs
                   font-semibold text-white 2xl:text-sm"
                    >
                      {Memento.interactions ? Memento.interactions : "-"}
                    </span>
                  </Table.Description>

                  <Table.Description>
                    <span
                      className="font-satoshi text-xs
                   font-semibold text-white 2xl:text-sm"
                    >
                      {getDropStatus(Memento)}
                    </span>
                  </Table.Description>
                  <Table.Description>
                    <span>{getMementoTableButton(Memento)}</span>
                  </Table.Description>
                </Table.Row>
              ))}
            </Table.Body>
          )}
        </Table>
      </div>
    </div>
  );
}

export default ActivationTable;
